import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import Documents from './components/Documents';
import UploadFilledForm from './components/UploadFilledForm';
import WorkerLogin from './components/WorkerLogin';
import WorkerDashboard from './components/WorkerDashboard';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import AllSubmissions from './components/AllSubmissions';
import Workers from './components/Workers';
import AdminWorkers from './components/AdminWorkers';
import ManageDocuments from './components/ManageDocuments';
import DatabaseManagement from './components/DatabaseManagement';
import ChangePassword from './components/ChangePassword';
import PDFWorkflowDemo from './components/PDFWorkflowDemo';
import UpdateNotifier from './components/UpdateNotifier';
import EmailSettings from './pages/EmailSettings';
import './App.css';

function App() {
  const [adminUser, setAdminUser] = useState(null);
  const [workerUser, setWorkerUser] = useState(null);

  useEffect(() => {
    // Check if admin is logged in from localStorage
    const storedAdminUser = localStorage.getItem('admin_user');
    if (storedAdminUser) {
      try {
        setAdminUser(JSON.parse(storedAdminUser));
      } catch (e) {
        localStorage.removeItem('admin_user');
      }
    }

    // Check if worker is logged in from localStorage
    const storedWorkerUser = localStorage.getItem('worker_user');
    if (storedWorkerUser) {
      try {
        setWorkerUser(JSON.parse(storedWorkerUser));
      } catch (e) {
        localStorage.removeItem('worker_user');
      }
    }
  }, []);

  const handleAdminLogin = (user) => {
    setAdminUser(user);
  };

  const handleAdminLogout = () => {
    setAdminUser(null);
  };

  const handleWorkerLogin = (user) => {
    setWorkerUser(user);
  };

  const handleWorkerLogout = () => {
    setWorkerUser(null);
  };

  return (
    <Router>
      <div className="App">
        <UpdateNotifier />
        <Routes>
          {/* Landing Page - Public */}
          <Route path="/" element={<LandingPage />} />

          {/* Documents Page - Protected (Worker Login Required) */}
          <Route
            path="/documents"
            element={
              workerUser ? (
                <Documents user={workerUser} onLogout={handleWorkerLogout} />
              ) : (
                <Navigate to="/worker/login" replace />
              )
            }
          />

          {/* PDF Workflow Demo - Public */}
          <Route path="/pdf-demo" element={<PDFWorkflowDemo />} />

          {/* Upload Filled Form Page - Protected (Worker Login Required) */}
          <Route
            path="/upload-form"
            element={
              workerUser ? (
                <UploadFilledForm user={workerUser} onLogout={handleWorkerLogout} />
              ) : (
                <Navigate to="/worker/login" replace />
              )
            }
          />

          {/* Worker Login */}
          <Route
            path="/worker/login"
            element={
              workerUser ? (
                <Navigate to="/worker/dashboard" replace />
              ) : (
                <WorkerLogin onLogin={handleWorkerLogin} />
              )
            }
          />

          {/* Worker Dashboard - Protected */}
          <Route
            path="/worker/dashboard"
            element={
              workerUser ? (
                <WorkerDashboard user={workerUser} onLogout={handleWorkerLogout} />
              ) : (
                <Navigate to="/worker/login" replace />
              )
            }
          />

          {/* Admin Login */}
          <Route
            path="/admin"
            element={
              adminUser ? (
                <Navigate to="/admin/dashboard" replace />
              ) : (
                <AdminLogin onLogin={handleAdminLogin} />
              )
            }
          />

          {/* Admin Dashboard - Protected */}
          <Route
            path="/admin/dashboard"
            element={
              adminUser ? (
                <AdminDashboard user={adminUser} onLogout={handleAdminLogout} />
              ) : (
                <Navigate to="/admin" replace />
              )
            }
          />

          {/* Admin Dashboard - Alternative route without /admin prefix */}
          <Route
            path="/dashboard"
            element={
              adminUser ? (
                <AdminDashboard user={adminUser} onLogout={handleAdminLogout} />
              ) : (
                <Navigate to="/admin" replace />
              )
            }
          />

          {/* All Submissions - Protected */}
          <Route
            path="/admin/all-submissions"
            element={
              adminUser ? (
                <AllSubmissions user={adminUser} onLogout={handleAdminLogout} />
              ) : (
                <Navigate to="/admin" replace />
              )
            }
          />

          {/* Email Settings - Protected */}
          <Route
            path="/admin/email-settings"
            element={
              adminUser ? (
                <EmailSettings user={adminUser} onLogout={handleAdminLogout} />
              ) : (
                <Navigate to="/admin" replace />
              )
            }
          />

          {/* Workers - Protected */}
          <Route
            path="/workers"
            element={
              adminUser ? (
                <Workers user={adminUser} onLogout={handleAdminLogout} />
              ) : (
                <Navigate to="/admin" replace />
              )
            }
          />

          {/* Workers - Alternative route with /admin prefix */}
          <Route
            path="/admin/workers"
            element={
              adminUser ? (
                <Workers user={adminUser} onLogout={handleAdminLogout} />
              ) : (
                <Navigate to="/admin" replace />
              )
            }
          />

          {/* Manage Documents - Protected */}
          <Route
            path="/admin/manage-documents"
            element={
              adminUser ? (
                <ManageDocuments user={adminUser} onLogout={handleAdminLogout} />
              ) : (
                <Navigate to="/admin" replace />
              )
            }
          />

          {/* Database Management - Protected */}
          <Route
            path="/admin/database"
            element={
              adminUser ? (
                <DatabaseManagement user={adminUser} onLogout={handleAdminLogout} />
              ) : (
                <Navigate to="/admin" replace />
              )
            }
          />

          {/* Change Password - Protected */}
          <Route
            path="/admin/change-password"
            element={
              adminUser ? (
                <ChangePassword user={adminUser} onLogout={handleAdminLogout} />
              ) : (
                <Navigate to="/admin" replace />
              )
            }
          />

          {/* Reports - Protected (placeholder, redirects to dashboard for now) */}
          <Route
            path="/reports"
            element={
              adminUser ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/admin" replace />
              )
            }
          />

          {/* Fallback - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
